import { ReactNode, useEffect, useState } from "react";

import { isEqual } from "lodash";

import { SelectionContext, TSelectionContextType } from "./context";

interface SelectionProviderProps<T> {
	children: ReactNode | ((props: TSelectionContextType<T>) => ReactNode);
	getKey: (item: T) => string | number;
	selectable?: T[];
	selectMode?: boolean;
	resetOnEmpty?: boolean;
}

export const SelectionProvider = <T,>({
	children,
	getKey,
	selectable = [],
	selectMode = false,
	resetOnEmpty = true,
}: SelectionProviderProps<T>) => {
	const [selected, setSelected] = useState<Map<string | number, T>>(
		new Map()
	);
	const [SelectMode, setSelectMode] = useState(selectMode);

	const selectItem = (item: T) => {
		const key = getKey(item);
		setSelected((prevSelected) => {
			const newSelected = new Map(prevSelected);
			if (newSelected.has(key)) {
				newSelected.delete(key);
			} else {
				newSelected.set(key, item);
			}
			return newSelected;
		});
	};

	const clearSelection = () => {
		setSelected(new Map());
	};

	const isSelected = (item: T) => {
		return selected.has(getKey(item));
	};

	const isAllSelected = () => {
		return selectable.every((item) => isSelected(item));
	};

	const isIndeterminate = () => {
		return !isAllSelected() && selectable.some((item) => isSelected(item));
	};

	const selectAll = () => {
		const newSelected = new Map(selected);
		selectable.forEach((item) => {
			const key = getKey(item);
			if (!newSelected.has(key)) {
				newSelected.set(key, item);
			}
		});
		setSelected(newSelected);
	};

	useEffect(() => {
		setSelected((prevSelected) => {
			const selectableByKey = new Map(
				selectable.map((item) => [getKey(item), item])
			);
			// Drop any previously-selected item no longer in `selectable`
			// (refreshing the rest to their latest object reference) —
			// this is what lets `resetOnEmpty` below ever actually see an
			// empty selection once everything selectable is gone.
			const newSelected = new Map<string | number, T>();
			prevSelected.forEach((_, key) => {
				const item = selectableByKey.get(key);
				if (item !== undefined) {
					newSelected.set(key, item);
				}
			});

			if (
				!isEqual(
					Array.from(newSelected.keys()),
					Array.from(prevSelected.keys())
				)
			) {
				if (!newSelected.size && resetOnEmpty) {
					setSelectMode(false);
				}
				return newSelected;
			}
			return prevSelected;
		});
	}, [selectable, getKey, resetOnEmpty]);

	useEffect(() => {
		if (!selectMode) {
			clearSelection();
		}
		setSelectMode(selectMode);
	}, [selectMode]);

	const values: TSelectionContextType<T> = {
		selected,
		isSelected,
		selectItem,
		clearSelection,
		isAllSelected,
		isIndeterminate,
		selectAll,
		selectMode: SelectMode,
		setSelectMode,
	};

	return (
		<SelectionContext.Provider
			// SelectionContext itself can't be generic (React.createContext
			// isn't) — it's fixed to TSelectionContextType<object>, so every
			// actual T needs this cast at the boundary. useSelection casts
			// symmetrically on the read side.
			value={values as unknown as TSelectionContextType<object>}
		>
			{typeof children === "function" ? children(values) : children}
		</SelectionContext.Provider>
	);
};
